import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import httpClient from "../../services/httpClient";
import Logger from "../../utils/Logger";
import { useAuth } from "../../AuthContext";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import deleteButtonIcon from "../../media/delete-button.svg";
import trashButtonIcon from "../../media/trash-button.svg";
import editButtonIcon from "../../media/edit-button.svg";
import agreeButtonIcon from "../../media/agree-button.svg";
import backButtonIcon from "../../media/back-button.svg";
import "../../styleSheet/main/admin/manageCars/manageCars.scss";
import { createPortal } from "react-dom";

const ManageCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [carToDelete, setCarToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Sprawdź czy użytkownik jest adminem
  useEffect(() => {
    if (userData.userRole !== "admin") {
      navigate("/");
    }
  }, [userData, navigate]);

  const fetchCars = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get("/cars", { withAuth: true });
      setCars(response.data);
      setError(null);
    } catch (err) {
      Logger.error("Błąd podczas pobierania samochodów:", err);
      setError("Nie udało się pobrać listy samochodów");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const handleAddCar = () => {
    navigate("/administrator/samochody/nowy-samochod");
  };

  const handleEditClick = (car) => {
    setEditingId(car.id);
    setEditValues({
      make: car.make,
      model: car.model,
      licensePlate: car.licensePlate,
      colorHex: car.colorHex || "#ff9900",
    });
  };

  const handleEditChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgreeEdit = async (car) => {
    try {
      await httpClient.put(
        `/Cars/${car.id}`,
        {
          make: editValues.make,
          model: editValues.model,
          licensePlate: editValues.licensePlate.toUpperCase(),
          colorHex: editValues.colorHex,
        },
        { withAuth: true },
      );
      setEditingId(null);
      setEditValues({});
      fetchCars();
    } catch (err) {
      Logger.error("Błąd podczas edycji samochodu:", err);
      setError("Nie udało się zaktualizować samochodu");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleDeleteClick = (car) => {
    setCarToDelete(car);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!carToDelete) return;

    setIsDeleting(true);
    try {
      await httpClient.delete(`/Cars/${carToDelete.id}`, { withAuth: true });
      setShowDeleteConfirmation(false);
      setCarToDelete(null);
      // Odśwież listę samochodów
      fetchCars();
    } catch (err) {
      Logger.error("Błąd podczas usuwania samochodu:", err);
      setError("Nie udało się usunąć samochodu");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setCarToDelete(null);
  };

  const handleBackClick = () => {
    navigate("/rezerwacje/samochody");
  };

  const renderDeleteConfirmationModal = () => {
    if (!showDeleteConfirmation || !carToDelete) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancelDelete}
          />
          <p>Czy na pewno chcesz usunąć samochód?</p>
          <div className="confirmation-details">
            <p>
              <strong>Marka:</strong> {carToDelete.make}
            </p>
            <p>
              <strong>Model:</strong> {carToDelete.model}
            </p>
            <p>
              <strong>Numer rejestracyjny:</strong> {carToDelete.licensePlate}
            </p>
            <p className="warning-text">
              <strong>Uwaga:</strong> Usunięcie samochodu spowoduje również
              usunięcie wszystkich związanych z nim rezerwacji!
            </p>
          </div>
          <div className="confirmation-buttons">
            {isDeleting ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Usuwanie...</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirmDelete}>TAK</button>
                <button onClick={handleCancelDelete}>NIE</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  if (loading) {
    return (
      <div className="manage-cars-container">
        <p>Ładowanie...</p>
      </div>
    );
  }

  return (
    <div className="manage-cars-container">
      <div className="manage-cars-header">
        <h1>Zarządzanie samochodami</h1>
        <div
          className="orange-sort-icon"
          onClick={handleBackClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <button className="add-car-button" onClick={handleAddCar}>
        Dodaj nowy samochód
      </button>

      {error && <div className="error-message">{error}</div>}

      <div className="cars-list">
        {cars.length === 0 ? (
          <div className="no-cars">Brak samochodów w bazie danych</div>
        ) : (
          <table className="cars-table">
            <thead>
              <tr>
                <th>Marka</th>
                <th>Model</th>
                <th>Numer rejestracyjny</th>
                <th className="color-column">Kolor</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {cars.map((car) => (
                <tr key={car.id}>
                  {editingId === car.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          className="table-edit-input"
                          value={editValues.make}
                          onChange={(e) =>
                            handleEditChange("make", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-edit-input"
                          value={editValues.model}
                          onChange={(e) =>
                            handleEditChange("model", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-edit-input"
                          value={editValues.licensePlate}
                          onChange={(e) =>
                            handleEditChange("licensePlate", e.target.value)
                          }
                        />
                      </td>
                      <td className="color-column">
                        <input
                          type="color"
                          className="table-color-input"
                          value={editValues.colorHex}
                          onChange={(e) =>
                            handleEditChange("colorHex", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <div className="actions">
                          <img
                            className="delete-btn"
                            src={trashButtonIcon}
                            alt="Usuń"
                            onClick={() => handleDeleteClick(car)}
                          />
                          <img
                            className="agree-btn"
                            src={agreeButtonIcon}
                            alt="Zatwierdź"
                            onClick={() => handleAgreeEdit(car)}
                          />
                          <img
                            className="back-btn"
                            src={backButtonIcon}
                            alt="Anuluj"
                            onClick={handleCancelEdit}
                          />
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{car.make}</td>
                      <td>{car.model}</td>
                      <td>{car.licensePlate}</td>
                      <td className="color-column">
                        {car.colorHex && (
                          <span
                            className="color-swatch"
                            style={{ backgroundColor: car.colorHex }}
                            title={car.colorHex}
                          />
                        )}
                      </td>
                      <td className="actions">
                        <img
                          className="delete-btn"
                          src={trashButtonIcon}
                          alt="Usuń"
                          onClick={() => handleDeleteClick(car)}
                        />
                        <img
                          className="edit-btn"
                          src={editButtonIcon}
                          alt="Edytuj"
                          onClick={() => handleEditClick(car)}
                        />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {renderDeleteConfirmationModal()}
    </div>
  );
};

export default ManageCars;
